import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';

import Modal from './Modal';
import ThemeContext from '../../theme/ThemeContext';
import ThemeIcons from '../../theme/Icons';
import { convertTimestampToPickerDate, convertPickerDateToTimestamp } from '../../lib/dateTimeHelper';

import 'react-datepicker/dist/react-datepicker.css';

const StyledDatePickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  .react-datepicker-wrapper {
    width: 100%;
  }
  
  .react-datepicker__input-container {
    width: 100%;
  }
  
  .react-datepicker__input-container input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${(props) => props.theme.borderColor};
    border-radius: ${(props) => props.theme.borderRadius}px;
    font-size: 14px;
    font-family: ${(props) => props.theme.fontFamily};
    background-color: ${(props) => props.theme.backgroundColor};
    color: ${(props) => props.theme.fontColorPrimary};
    
    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.hoverColor};
    }
  }

  .react-datepicker__day-name {
    font-weight: 600;
  }

  .react-datepicker__day--outside-month {
    opacity: 0.5;
  }
  
  .react-datepicker {
    display: flex;
    align-items: stretch;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.borderColor};
    border-radius: ${(props) => props.theme.borderRadius}px;
    font-family: ${(props) => props.theme.fontFamily};
    background-color: ${(props) => props.theme.backgroundColor};
  }

  .react-datepicker__month-container {
    float: none;
    display: flex;
    flex-direction: column;
  }
  
  .react-datepicker__header  {
    background-color: ${(props) => props.theme.backgroundColor};
    border-bottom: 1px solid ${(props) => props.theme.borderColor};
  }
  
  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker__day-name {
    color: ${(props) => props.theme.fontColorPrimary};
  }
  
  .react-datepicker__day {
    color: ${(props) => props.theme.fontColorPrimary};

    &.react-datepicker__day--keyboard-selected {
      background-color: transparent;
    }
    
    &:hover {
      background-color: ${(props) => props.theme.primaryColor};
      color: ${(props) => props.theme.backgroundColor};
      border-radius: ${(props) => props.theme.borderRadius}px;
    }
    
    &.react-datepicker__day--selected {
      background-color: ${(props) => props.theme.primaryColor};
      color: ${(props) => props.theme.backgroundColor};
      border-radius: ${(props) => props.theme.borderRadius}px;
    }
    
    &.react-datepicker__day--today {
      background-color: ${(props) => props.theme.borderColor};
      border: 1px solid ${(props) => props.theme.borderColor};
      border-radius: ${(props) => props.theme.borderRadius}px;

    }
  }

  .react-datepicker__time-container {
    float: none;
    width: 96px;
    display: flex;
    flex-direction: column;
    border-left: 1px solid ${(props) => props.theme.borderColor};
    overflow: hidden;
  }

  /*
   * In datetime mode the calendar drives the picker height and the time column
   * stretches to match it (see .react-datepicker__time-list below). In time-only
   * mode there is no calendar, so give the time column a definite height for the
   * absolutely-positioned, internally-scrolling time list to fill.
   */
  .react-datepicker--time-only .react-datepicker__time-container {
    height: 280px;
    border-left: none;
  }

  .react-datepicker__header--time {
    min-height: 53px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 0 12px;
    box-sizing: border-box;
    font-weight: 600;
  }

  .react-datepicker__time {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background-color: ${(props) => props.theme.backgroundColor};
  }

  .react-datepicker__time-box {
    width: 100% !important;
    flex: 1;
    min-height: 0;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .react-datepicker__time-list {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: auto !important;
    overflow-y: auto !important;

    .react-datepicker__time-list-item.react-datepicker__time-list-item--selected {
      background-color: ${(props) => props.theme.primaryColor} !important;
      color: ${(props) => props.theme.backgroundColor} !important;
    }

    .react-datepicker__time-list-item {
      color: ${(props) => props.theme.fontColorPrimary};
      
      &:hover {
        background-color: ${(props) => props.theme.primaryColor} !important;
        color: ${(props) => props.theme.backgroundColor} !important;
      }
      
      &.react-datepicker__time-list-item--selected {
        background-color: ${(props) => props.theme.primaryColor};
        color: ${(props) => props.theme.backgroundColor};
      }
    }
  }
  .react-datepicker__current-month {
    display: none;
  }
  
  .react-datepicker__navigation {
    display: none;
  }

  .react-datepicker__header--custom {
    background-color: ${(props) => props.theme.backgroundColor};
    border-bottom: 1px solid ${(props) => props.theme.borderColor};
    min-height: 53px;
    padding: 8px 12px 12px;
    box-sizing: border-box;
  }

`;

const StyledCustomHeader = styled.div`
  position: relative;
`;

const StyledCustomHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const StyledNavigationButton = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: ${(props) => props.theme.borderRadius}px;
  background: ${(props) => props.theme.backgroundColor};
  color: ${(props) => props.theme.fontColorPrimary};
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.borderColor};
  }
`;

const StyledHeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledHeaderToggle = styled.button`
  min-width: 88px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${(props) => props.$isOpen ? props.theme.primaryColor : props.theme.borderColor};
  border-radius: ${(props) => props.theme.borderRadius}px;
  background: ${(props) => props.$isOpen ? props.theme.primaryColor : props.theme.backgroundColor};
  color: ${(props) => props.$isOpen ? props.theme.backgroundColor : props.theme.fontColorPrimary};
  font-family: ${(props) => props.theme.fontFamily};
  font-size: 14px;
  cursor: pointer;

  &:hover {
    border-color: ${(props) => props.theme.primaryColor};
  }
`;

const StyledOptionsPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: ${(props) => props.$align === 'right' ? 'auto' : '0'};
  right: ${(props) => props.$align === 'right' ? '0' : 'auto'};
  z-index: 2;
  width: ${(props) => props.$width}px;
  max-height: 190px;
  overflow-y: auto;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: ${(props) => props.theme.borderRadius}px;
  background: ${(props) => props.theme.backgroundColor};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
`;

const StyledOptionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  background: ${(props) => props.$isSelected ? props.theme.borderColor : 'transparent'};
  color: ${(props) => props.theme.fontColorPrimary};
  font-family: ${(props) => props.theme.fontFamily};
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.primaryColor};
    color: ${(props) => props.theme.backgroundColor};
  }
`;


/**
 * Detects the picker type based on the day.js format string
 * @param {string} format - Day.js format string
 * @returns {string} - 'date', 'time', or 'datetime'
 */
const detectPickerType = (format) => {
  if (!format || typeof format !== 'string') {
    return 'date'; // Default to date picker
  }
  const hasDate = /[YMD]/.test(format);
  const hasTime = /[hHmsa]/.test(format);

  if (hasDate && hasTime) return 'datetime';
  if (hasTime) return 'time';
  return 'date';
};

/**
 * Detects if the format uses 12-hour (AM/PM) or 24-hour time
 * Based on Unicode Technical Standard #35 and day.js documentation:
 * - Lowercase 'h' or 'hh' = 12-hour format
 * - Uppercase 'H' or 'HH' = 24-hour format
 * @param {string} format - Day.js format string
 * @returns {boolean} - true for 12-hour format, false for 24-hour format
 */
const is12HourFormat = (format) => {
  if (!format || typeof format !== 'string') {
    return false;
  }
  return /h/.test(format);
};

/**
 * Converts day.js format to react-datepicker format
 * @param {string} dayjsFormat - Day.js format string
 * @returns {string} - React-datepicker format string
 */
const convertDayjsFormatToDatePickerFormat = (dayjsFormat) => {
  if (!dayjsFormat || typeof dayjsFormat !== 'string') {
    return 'MM/dd/yyyy'; // Default format
  }
  return dayjsFormat
    .replace(/YYYY/g, 'yyyy')
    .replace(/MM/g, 'MM')
    .replace(/DD/g, 'dd')
    .replace(/hh/g, 'hh')
    .replace(/mm/g, 'mm')
    .replace(/ss/g, 'ss')
    .replace(/A/g, 'aa');
};

const DateTimePickerModal = ({
  isOpen,
  value = null,
  format,
  onSubmit,
  onClose,
  onClear,
  timeIntervals = 1,
  allowKeyboardInput = true,
  timezone
}) => {
  const theme = useContext(ThemeContext);
  const headerRef = useRef(null);
  const selectedMonthOptionRef = useRef(null);
  const selectedYearOptionRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    if (value && typeof value === 'number') {
      return convertTimestampToPickerDate(value, timezone, format);
    }
    return new Date();
  });
  const [openPanel, setOpenPanel] = useState(null);

  const pickerType = detectPickerType(format);
  const datePickerFormat = convertDayjsFormatToDatePickerFormat(format);
  const use12HourFormat = is12HourFormat(format);
  const modalDimensions = useMemo(() => {
    switch (pickerType) {
      case 'datetime':
        return { width: 380, height: 'auto' };
      case 'time':
        return { width: 360, height: 'auto' };
      default:
        return { width: 360, height: 360 };
    }
  }, [pickerType]);
  const monthLabels = useMemo(() => Array.from({ length: 12 }, (_, monthIndex) => (
    new Intl.DateTimeFormat(undefined, { month: 'long' }).format(new Date(2024, monthIndex, 1))
  )), []);
  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = 1900; year <= 2100; year += 1) {
      years.push(year);
    }
    return years;
  }, []);

  /**
   * Only reset the month/year panel when the modal closes. We intentionally do
   * NOT resync `selectedDate` here: the modal instance stays mounted across
   * close/reopen, and (matching master) any unsaved in-progress selection must
   * be preserved. `selectedDate` is seeded once by the useState initializer and
   * thereafter changes only when the user picks a day/time.
   */
  useEffect(() => {
    if (!isOpen) {
      setOpenPanel(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!openPanel) return undefined;

    const selectedOptionRef = openPanel === 'month' ? selectedMonthOptionRef : selectedYearOptionRef;
    const optionEl = selectedOptionRef.current;
    if (optionEl) {
      // Center the selected option within the dropdown panel only. Using
      // scrollIntoView here would also scroll ancestor scroll containers
      // (e.g. .react-datepicker, which has overflow: hidden), shifting the
      // calendar content. The option's offsetParent is StyledOptionsPanel
      // (position: absolute), so offsetTop is relative to the panel.
      const panelEl = optionEl.offsetParent;
      if (panelEl) {
        panelEl.scrollTop = optionEl.offsetTop - (panelEl.clientHeight - optionEl.offsetHeight) / 2;
      }
    }

    const handleOutsideMouseDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setOpenPanel(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideMouseDown);
    return () => document.removeEventListener('mousedown', handleOutsideMouseDown);
  }, [openPanel]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenPanel(null);
  };

  const handleSubmit = () => {
    if (selectedDate && onSubmit) {
      const timestamp = convertPickerDateToTimestamp(selectedDate, timezone, format);
      onSubmit(timestamp);
      onClose();
    }
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
      onClose();
    }
  };

  const renderCustomHeader = ({
    monthDate,
    changeMonth,
    changeYear,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
  }) => {
    const activeMonth = monthDate.getMonth();
    const activeYear = monthDate.getFullYear();

    /**
     * Month/year navigation only changes the *visible* calendar month, which
     * react-datepicker owns via these callbacks (they update its internal view
     * state, surfaced back to us as `monthDate`). We deliberately do NOT touch
     * `selectedDate` here — the saved value should only change when the user
     * clicks a day/time. Mutating it with native Date#setMonth/setFullYear also
     * overflows end-of-month dates (e.g. Jan 31 -> February becomes Mar 2/3).
     */
    const handleMonthChange = (month) => {
      changeMonth(month);
      setOpenPanel(null);
    };

    const handleYearChange = (year) => {
      changeYear(year);
      setOpenPanel(null);
    };

    const handlePreviousMonth = () => {
      decreaseMonth();
      setOpenPanel(null);
    };

    const handleNextMonth = () => {
      increaseMonth();
      setOpenPanel(null);
    };

    return (
      <StyledCustomHeader ref={headerRef}>
        <StyledCustomHeaderRow>
          <StyledNavigationButton
            type="button"
            onClick={handlePreviousMonth}
            disabled={prevMonthButtonDisabled}
            theme={theme}
          >
            {'<'}
          </StyledNavigationButton>
          <StyledHeaderControls>
            <StyledHeaderToggle
              type="button"
              onClick={() => setOpenPanel((currentPanel) => currentPanel === 'month' ? null : 'month')}
              $isOpen={openPanel === 'month'}
              theme={theme}
            >
              {monthLabels[activeMonth]}
            </StyledHeaderToggle>
            <StyledHeaderToggle
              type="button"
              onClick={() => setOpenPanel((currentPanel) => currentPanel === 'year' ? null : 'year')}
              $isOpen={openPanel === 'year'}
              theme={theme}
            >
              {activeYear}
            </StyledHeaderToggle>
          </StyledHeaderControls>
          <StyledNavigationButton
            type="button"
            onClick={handleNextMonth}
            disabled={nextMonthButtonDisabled}
            theme={theme}
          >
            {'>'}
          </StyledNavigationButton>
        </StyledCustomHeaderRow>
        {openPanel === 'month' ? (
          <StyledOptionsPanel $align="left" $width={170} theme={theme}>
            {monthLabels.map((monthLabel, monthIndex) => (
              <StyledOptionButton
                key={monthLabel}
                type="button"
                onClick={() => handleMonthChange(monthIndex)}
                $isSelected={monthIndex === activeMonth}
                ref={monthIndex === activeMonth ? selectedMonthOptionRef : null}
                theme={theme}
              >
                <span>{monthLabel}</span>
                {monthIndex === activeMonth ? <span>✓</span> : null}
              </StyledOptionButton>
            ))}
          </StyledOptionsPanel>
        ) : null}
        {openPanel === 'year' ? (
          <StyledOptionsPanel $align="right" $width={120} theme={theme}>
            {yearOptions.map((year) => (
              <StyledOptionButton
                key={year}
                type="button"
                onClick={() => handleYearChange(year)}
                $isSelected={year === activeYear}
                ref={year === activeYear ? selectedYearOptionRef : null}
                theme={theme}
              >
                <span>{year}</span>
                {year === activeYear ? <span>✓</span> : null}
              </StyledOptionButton>
            ))}
          </StyledOptionsPanel>
        ) : null}
      </StyledCustomHeader>
    );
  };

  const renderDatePicker = () => {
    const commonProps = {
      selected: selectedDate,
      onChange: handleDateChange,
      dateFormat: datePickerFormat,
      showPopperArrow: false,
      inline: true,
      allowSameDay: true,
      allowKeyboardNavigation: allowKeyboardInput,
      enableTabLoop: false,
      renderCustomHeader,
    };


    switch (pickerType) {
      case 'date':
        return (
          <DatePicker
            {...commonProps}
            showTimeSelect={false}
            showTimeSelectOnly={false}
          />
        );

      case 'time':
        return (
          <DatePicker
            {...commonProps}
            showTimeSelect={true}
            showTimeSelectOnly={true}
            timeFormat={use12HourFormat ? "hh:mm aa" : "HH:mm"}
            timeIntervals={timeIntervals}
            timeCaption="Time"
          />
        );

      case 'datetime':
        return (
          <DatePicker
            {...commonProps}
            showTimeSelect={true}
            showTimeSelectOnly={false}
            timeFormat={use12HourFormat ? "hh:mm aa" : "HH:mm"}
            timeIntervals={timeIntervals}
            timeCaption="Time"
            dateFormat={`${datePickerFormat} ${use12HourFormat ? "hh:mm aa" : "HH:mm"}`}
          />
        );

      default:
        return (
          <DatePicker
            {...commonProps}
            showTimeSelect={false}
            showTimeSelectOnly={false}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onHide={onClose}
      title="Select Date/Time"
      icon={ThemeIcons.misc.calendar}
      width={modalDimensions.width}
      height={modalDimensions.height}
      onSubmit={handleSubmit}
      submitText="Save"
      onCancel={value && onClear ? handleClear : undefined}
      cancelText={value && onClear ? "Clear" : undefined}
    >
      <StyledDatePickerWrapper theme={theme}>
        {renderDatePicker()}
      </StyledDatePickerWrapper>
    </Modal>
  );
};

DateTimePickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  value: PropTypes.number,
  format: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  timeIntervals: PropTypes.number,
  allowKeyboardInput: PropTypes.bool,
  timezone: PropTypes.string
};

export default DateTimePickerModal;
